import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api-client";
import { debug } from "@/lib/debug";

export type Market = {
  symbol: string;
  baseSymbol: string;
  quoteSymbol: string;
  providerSymbol: string;
  binanceSymbol?: string; // Deprecated - kept for backward compatibility
  basePrecision: number;
  quotePrecision: number;
  minOrderSize: number;
  minOrderValueUsd: number; // Minimum USD notional value per order (e.g., 10 means $10 minimum)
  tickSize: number;
  isActive: boolean;
  isFeatured: boolean;
  sortOrder: number;
  healthStatus: "healthy" | "stale" | "deprecated" | "error" | "unknown";
  lastPriceUpdate: string | null;
  lastHealthCheck: string | null;
  providerStatus: string | null;
  binanceStatus?: string | null; // Deprecated
  healthError: string | null;
  displayName: string | null;
  description: string | null;
  category: string | null;
  iconUrl: string | null;
};

/**
 * Hook to fetch all active markets from the backend
 * Returns ALL 75 markets from the database
 * Markets are cached for 1 minute to reduce API calls
 */
export const useMarkets = () => {
  return useQuery({
    queryKey: ["api-markets"],  // Unique key to avoid collision with solana/hooks/use-markets
    queryFn: async () => {
      debug.log('useMarkets', 'Fetching markets from API...');
      const markets = await apiClient.get<Market[]>("/api/markets");
      debug.log('useMarkets', `Received ${markets?.length || 0} markets from API`);
      if (markets && markets.length > 0) {
        debug.log('useMarkets', 'Sample market:', markets[0]);
      }
      return markets;
    },
    staleTime: 60000, // Cache for 1 minute
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

/**
 * Hook to get a specific market by symbol
 */
export const useMarket = (symbol: string | undefined) => {
  const { data: markets, ...rest } = useMarkets();
  
  const market = markets?.find((m: Market) => m.symbol === symbol);
  
  return {
    ...rest,
    data: market,
  };
};

/**
 * Hook to get featured markets (top 10)
 */
export const useFeaturedMarkets = () => {
  const { data: markets, ...rest } = useMarkets();
  
  const featured = markets?.filter((m: Market) => m.isFeatured) || [];
  
  return {
    ...rest,
    data: featured,
  };
};

